import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  User,
  Briefcase,
  Github,
  Globe2,
  Linkedin,
  Twitter,
  Edit,
  Building,
  MapPin,
  BadgeCheck,
  Share2,
  School,
  BookOpen,
  Layout,
  Award,
  Users,
  FileText,
  AlertTriangle,
  MessageSquare,
} from "lucide-react";

import ShareModal from "../components/ShareModal";
import ProfileActivities from "../components/ProfileActivities";
import ConnectionRequests from "../components/ConnectionRequest";
import ChatComponent from "../components/ChatComponent";
import AlmaCardButton from "../components/AlmaCard";

// Sub-components
const ProfileSection = ({ title, icon: Icon, children }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6">
    <h3 className="flex items-center text-lg font-semibold text-gray-900 mb-4">
      <Icon className="h-5 w-5 mr-2 text-blue-500" />
      {title}
    </h3>
    {children}
  </div>
);

const SocialLink = ({ platform, url, icon: Icon, color, hoverColor }) => {
  if (!url) return null;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={`flex items-center px-2 md:px-3 py-2 rounded-lg ${color} ${hoverColor} transition-colors text-white w-full md:w-auto justify-center`}
    >
      <Icon className="h-4 w-4 md:h-5 md:w-5" />
      <span className="ml-2 text-xs md:text-sm">{platform}</span>
    </a>
  );
};

const TabButton = ({ active, icon: Icon, label, onClick }) => (
  <button
    onClick={onClick}
    className={`flex items-center px-3 md:px-4 py-2 rounded-lg transition-colors whitespace-nowrap text-sm md:text-base ${
      active
        ? "bg-blue-50 text-blue-700 font-medium"
        : "text-gray-600 hover:bg-gray-50"
    }`}
  >
    <Icon className="h-4 w-4 md:h-5 md:w-5 mr-1 md:mr-2" />
    {label}
  </button>
);

// Main Component
const Profile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [connections, setConnections] = useState([]);

  useEffect(() => {
    if (activeTab === "connections") {
      fetchConnections();
    }
  }, [activeTab]);

  const fetchConnections = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/connections/accepted`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setConnections(response.data);
    } catch (err) {
      console.error("Error fetching connections:", err);
    }
  };
  const tabs = [
    { id: "overview", label: "Overview", icon: Layout },
    { id: "experience", label: "Experience", icon: Briefcase },
    { id: "education", label: "Education", icon: BookOpen },
    { id: "connections", label: "Connections", icon: Users },
    { id: "messages", label: "Messages", icon: MessageSquare },
    { id: "activities", label: "Activities", icon: Users },
  ];

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/alumni/profile`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setProfile(response.data);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load profile");
      console.error("Error fetching profile:", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="h-16 w-16 rounded-full border-4 border-blue-100 border-opacity-50"></div>
            <div className="absolute inset-0 h-16 w-16 rounded-full border-4 border-t-blue-500 animate-spin"></div>
          </div>
          <p className="mt-4 text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center bg-white p-8 rounded-xl shadow-lg max-w-md">
          <div className="w-16 h-16 mx-auto bg-red-100 rounded-full flex items-center justify-center">
            <AlertTriangle className="h-8 w-8 text-red-500" />
          </div>
          <h3 className="mt-4 text-lg font-semibold text-gray-900">
            Error Loading Profile
          </h3>
          <p className="mt-2 text-red-500">{error}</p>
          <button
            onClick={fetchProfile}
            className="mt-6 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case "overview":
        return (
          <div className="space-y-4 md:space-y-6">
            <ProfileSection title="Basic Information" icon={User}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-sm text-gray-500">Full Name</p>
                  <p className="font-medium">
                    {profile?.firstName} {profile?.lastName}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-gray-500">Degree</p>
                  <p className="font-medium">{profile?.academicInfo?.degree || "Not specified"}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-gray-500">Department</p>
                  <p className="font-medium">{profile?.academicInfo?.department || "Not specified"}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="font-medium break-words">{profile?.user?.email}</p>
                </div>
              </div>
            </ProfileSection>

            <ProfileSection title="Current Status" icon={FileText}>
              <div className="space-y-4">
                {profile?.professionalInfo?.experiences
                  ?.filter((exp) => exp.isCurrent)
                  .map((exp, index) => (
                    <div key={index} className="flex flex-col sm:flex-row sm:items-start space-y-2 sm:space-y-0 sm:space-x-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                        <Building className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">{exp.title}</h4>
                        <p className="text-sm text-gray-500">{exp.company}</p>
                        <p className="text-sm text-gray-500">{exp.location}</p>
                      </div>
                    </div>
                  ))}
              </div>
            </ProfileSection>
          </div>
        );

      case "experience":
        return (
          <ProfileSection title="Professional Experience" icon={Briefcase}>
            {profile?.professionalInfo?.experiences?.length ? (
              <div className="space-y-6">
                {profile.professionalInfo.experiences.map((exp, index) => (
                  <div
                    key={index}
                    className="relative pl-8 pb-6 last:pb-0 border-l-2 border-gray-200 last:border-l-0"
                  >
                    <div className="absolute left-0 top-0 -translate-x-1/2 w-4 h-4 rounded-full bg-white border-2 border-blue-500" />
                    <div className="bg-white rounded-lg p-4 hover:bg-gray-50 transition-colors">
                      <h4 className="font-medium text-gray-900 flex items-center">
                        {exp.title}
                        {exp.isCurrent && (
                          <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full">
                            Current
                          </span>
                        )}
                      </h4>
                      <p className="mt-1 text-sm text-gray-500">
                        {exp.company}
                      </p>
                      <div className="mt-2 flex items-center text-sm text-gray-500">
                        <MapPin className="h-4 w-4 mr-1" />
                        {exp.location}
                      </div>
                      {exp.description && (
                        <p className="mt-2 text-sm text-gray-600">
                          {exp.description}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Briefcase className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  No experience added
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Add your professional experience to showcase your career
                  journey
                </p>
              </div>
            )}
          </ProfileSection>
        );

      case "education":
        return (
          <ProfileSection title="Education" icon={School}>
            <div className="space-y-6">
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-gray-900">
                      {profile?.college?.name}
                    </h4>
                    <p className="mt-1 text-sm text-gray-500">
      Degree: {profile?.academicInfo?.degree || "Not specified"} • 
      Department: {profile?.academicInfo?.department || "Not specified"}
    </p>
                  </div>
                  {profile?.college?.logo && (
                    <img
                      src={profile.college.logo}
                      alt={profile.college.name}
                      className="h-12 w-auto"
                    />
                  )}
                </div>
                <div className="mt-4 grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-sm text-gray-500">Enrollment Number</p>
                    <p className="font-medium">{profile?.enrollmentNumber}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-sm text-gray-500">Graduation Year</p>
                    <p className="font-medium">{profile?.graduationYear}</p>
                  </div>
                </div>
              </div>
            </div>
          </ProfileSection>
        );

   

        case "connections":
          return (
            <div className="space-y-6">
              {/* Pending Requests */}
              <ProfileSection title="Connection Requests" icon={Users}>
                <ConnectionRequests 
                  onRequestHandled={(acceptedConnection) => {
                    // Add the newly accepted connection to the connections list
                    setConnections(prev => [{
                      _id: acceptedConnection._id,
                      firstName: acceptedConnection.sender.firstName,
                      lastName: acceptedConnection.sender.lastName,
                      department: acceptedConnection.sender.department,
                      personalInfo: acceptedConnection.sender.personalInfo
                    }, ...prev]);
                  }} 
                />
              </ProfileSection>
        
              {/* Connected Alumni */}
              <ProfileSection title="Your Connections" icon={Users}>
                {connections.length === 0 ? (
                  <div className="text-center py-8">
                    <Users className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">
                      No connections yet
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Start connecting with other alumni to grow your network
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {connections.map((connection) => (
                      <div
                        key={connection._id}
                        className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        {connection.personalInfo?.avatar ? (
                          <img
                            src={connection.personalInfo.avatar}
                            alt={`${connection.firstName} ${connection.lastName}`}
                            className="h-12 w-12 rounded-full object-cover"
                          />
                        ) : (
                          <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                            <User className="h-6 w-6 text-blue-600" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-gray-900 truncate">
                            {connection.firstName} {connection.lastName}
                          </h4>
                          <p className="text-sm text-gray-500 truncate">
                            {connection.department}
                          </p>
                        </div>
                        <Link
                          to={`/chat/${connection._id}`}
                          className="p-2 text-blue-600 hover:bg-blue-100 rounded-full flex-shrink-0"
                        >
                          <MessageSquare className="h-5 w-5" />
                        </Link>
                      </div>
                    ))}
                  </div>
                )}
              </ProfileSection>
            </div>
          );
      case "messages":
        return (
          <ProfileSection title="Messages" icon={MessageSquare}>
            <ChatComponent />
          </ProfileSection>
        );
      case "activities":
        return <ProfileActivities />;

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
    {/* Responsive Profile Header */}
    <div className="relative">
      <div 
        className="h-32 sm:h-48"
        style={{
          backgroundImage: `url('https://i.ibb.co/zfWnYCB/image.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }} 
      />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative -mt-16 sm:-mt-24">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
              <div className="flex flex-col sm:flex-row sm:space-x-5 items-center sm:items-start">
                <div className="relative flex-shrink-0">
                  {profile?.personalInfo?.avatar ? (
                    <img
                      src={profile.personalInfo.avatar}
                      alt={profile?.firstName}
                      className="h-16 w-16 sm:h-20 sm:w-20 rounded-full ring-4 ring-white"
                    />
                  ) : (
                    <div className="h-16 w-16 sm:h-20 sm:w-20 rounded-full bg-blue-100 ring-4 ring-white flex items-center justify-center">
                      <User className="h-8 w-8 sm:h-10 sm:w-10 text-blue-600" />
                    </div>
                  )}
                  {profile?.isVerified && (
                    <div className="absolute bottom-0 right-0 bg-blue-500 rounded-full p-1 ring-2 ring-white">
                      <BadgeCheck className="h-3 w-3 sm:h-4 sm:w-4 text-white" />
                    </div>
                  )}
                </div>
                <div className="text-center sm:text-left mt-2 sm:mt-0">
                  <h1 className="text-lg sm:text-xl font-bold text-gray-900">
                    {profile?.firstName} {profile?.lastName}
                  </h1>
                  <p className="mt-1 text-sm text-gray-500">
                    {profile?.academicInfo?.degree} • {profile?.academicInfo?.department}
                  </p>
                  <p className="text-sm text-gray-500">{profile?.college?.name}</p>
                </div>
              </div>
              
              <div className="flex flex-wrap justify-center sm:justify-end gap-2">
                <AlmaCardButton profile={profile} />
                <Link
                  to="/profile/edit"
                  className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Link>
                <button
                  onClick={() => setIsShareModalOpen(true)}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  <Share2 className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Responsive Social Links */}
            {(profile?.socialLinks?.linkedin ||
              profile?.socialLinks?.github ||
              profile?.socialLinks?.twitter ||
              profile?.socialLinks?.portfolio) && (
              <div className="mt-4 sm:mt-6 grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
                <SocialLink
                  platform="LinkedIn"
                  url={profile?.socialLinks?.linkedin}
                  icon={Linkedin}
                  color="bg-[#0A66C2]"
                  hoverColor="hover:bg-[#004182]"
                />
                <SocialLink
                  platform="GitHub"
                  url={profile?.socialLinks?.github}
                  icon={Github}
                  color="bg-[#24292F]"
                  hoverColor="hover:bg-[#1b1f23]"
                />
                <SocialLink
                  platform="Twitter"
                  url={profile?.socialLinks?.twitter}
                  icon={Twitter}
                  color="bg-[#1DA1F2]"
                  hoverColor="hover:bg-[#0c85d0]"
                />
                <SocialLink
                  platform="Portfolio"
                  url={profile?.socialLinks?.portfolio}
                  icon={Globe2}
                  color="bg-[#6366F1]"
                  hoverColor="hover:bg-[#4F46E5]"
                />
              </div>
            )}

            {/* Responsive Navigation Tabs */}
            <div className="mt-4 sm:mt-6 border-t border-gray-200 pt-4 sm:pt-6">
              <div className="flex overflow-x-auto hide-scrollbar space-x-2 sm:space-x-4">
                {tabs.map((tab) => (
                  <TabButton
                    key={tab.id}
                    active={activeTab === tab.id}
                    icon={tab.icon}
                    label={tab.label}
                    onClick={() => setActiveTab(tab.id)}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Tab Content with responsive padding */}
        <div className="mt-4 sm:mt-6 pb-8 sm:pb-12">
          {renderTabContent()}
        </div>
      </div>
    </div>

    {/* Share Modal */}
    <ShareModal
      isOpen={isShareModalOpen}
      onClose={() => setIsShareModalOpen(false)}
      profile={profile}
    />
  </div>
);
};

export default Profile;
